
import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Upload, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { OptimizedImage } from "@/components/ui/OptimizedImage";

interface AvatarSelectorProps {
  customAvatar: string | null;
  setCustomAvatar: (url: string | null) => void;
  displayName: string;
}

// Memoize component to prevent unnecessary renders
export const AvatarSelector = React.memo(function AvatarSelector({ 
  customAvatar, 
  setCustomAvatar,
  displayName 
}: AvatarSelectorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  
  const initial = React.useMemo(() => 
    getInitials(displayName), 
    [displayName]
  );
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Image must be less than 5MB",
        variant: "destructive",
      });
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setCustomAvatar(reader.result as string);
    };
    reader.readAsDataURL(file);
  };
  
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  
  const removeCustomAvatar = () => {
    setCustomAvatar(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  function getInitials(name: string) {
    return name?.trim().charAt(0).toUpperCase() || "?";
  }
  
  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        {customAvatar ? (
          <div className="relative" style={{ width: '80px', height: '80px' }}>
            <Avatar className="h-20 w-20 border-2 border-primary">
              <OptimizedImage
                src={customAvatar}
                alt="Custom avatar"
                width={80}
                height={80}
                className="rounded-full w-20 h-20 object-cover"
                priority
                style={{ objectFit: "cover" }}
              />
              <AvatarFallback>{initial}</AvatarFallback>
            </Avatar>
            <Button 
              type="button" 
              variant="destructive" 
              size="icon" 
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full" 
              onClick={removeCustomAvatar}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div style={{ width: '80px', height: '80px' }}>
              <Avatar className="h-20 w-20 bg-primary/10">
                <AvatarFallback className="text-xl font-semibold text-primary">
                  {initial}
                </AvatarFallback>
              </Avatar>
            </div>
            
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleUploadClick}
              className="flex gap-2 mt-2"
            >
              <Upload className="h-4 w-4" />
              Upload your photo
            </Button>
          </div>
        )}
      </div>
      
      <input 
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
});
